import { useState, ChangeEvent, FormEvent, SyntheticEvent } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Button, 
  TextField, 
  Grid, 
  CircularProgress,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Card,
  CardContent,
  Stack
} from '@mui/material';
import { 
  Send as SendIcon, 
  QuestionAnswer as QuestionIcon,
  Lightbulb as FeatureIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { generateClient } from 'aws-amplify/api';

const client = generateClient();

// Manually defining the mutation since codegen might not have run yet
const createContactSubmission = /* GraphQL */ `
  mutation CreateContactSubmission(
    $input: CreateContactSubmissionInput!
    $condition: ModelContactSubmissionConditionInput
  ) {
    createContactSubmission(input: $input, condition: $condition) {
      id
      type
      email
      subject
      message
      status
      owner
      createdAt
      updatedAt
    }
  }
`;

type SubmissionType = 'CONTACT' | 'QUESTION' | 'FEATURE_REQUEST';

interface FormData {
  email: string;
  subject: string;
  message: string;
}

const Contact = () => {
  const { user } = useAuthenticator((context) => [context.user]);
  const [tabValue, setTabValue] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    email: user?.signInDetails?.loginId || '',
    subject: '',
    message: '',
  });
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    // Reset subject/message but keep email
    setFormData(prev => ({
      ...prev,
      subject: '',
      message: ''
    }));
  };

  const handleChange = (field: keyof FormData) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const getSubmissionType = (): SubmissionType => {
    switch (tabValue) {
      case 0: return 'CONTACT';
      case 1: return 'QUESTION';
      case 2: return 'FEATURE_REQUEST';
      default: return 'CONTACT';
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailRegex.test(formData.email) || !formData.subject || !formData.message) {
      setSnackbar({ open: true, message: 'Please fill in all fields correctly', severity: 'error' });
      return;
    }

    setSubmitting(true);
    try {
      await client.graphql({
        query: createContactSubmission,
        variables: {
          input: {
            type: getSubmissionType(),
            email: formData.email,
            subject: formData.subject,
            message: formData.message,
            status: 'NEW',
          }
        }
      });

      setSnackbar({ 
        open: true, 
        message: 'Thank you! Your submission has been received.', 
        severity: 'success' 
      });
      
      // Reset form
      setFormData(prev => ({
        ...prev,
        subject: '',
        message: ''
      }));
    } catch (error) {
      console.error('Error submitting form:', error);
      setSnackbar({ open: true, message: 'Failed to send submission. Please try again.', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 900, margin: '0 auto' }}>
      <Typography variant="h4" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mb: 4 }}>
        Contact & Feedback
      </Typography>

      <Grid container spacing={4}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Stack spacing={3}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <EmailIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">General Inquiries</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  For formal communications, please reach out directly to our administration.
                </Typography>
                <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 'bold' }}>
                  info@matocs.org
                </Typography>
              </CardContent>
            </Card>

            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" gutterBottom>How it works</Typography>
                <Typography variant="body2" paragraph>
                  1. Choose the category that best fits your needs.
                </Typography>
                <Typography variant="body2" paragraph>
                  2. Provide as much detail as possible in your message.
                </Typography>
                <Typography variant="body2">
                  3. Our team will review your submission and follow up via email if required.
                </Typography>
              </CardContent>
            </Card>
          </Stack>
        </Grid>

        <Grid size={{ xs: 12, md: 8 }}>
          <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
              <Tabs 
                value={tabValue} 
                onChange={handleTabChange} 
                variant="fullWidth"
                indicatorColor="primary"
                textColor="primary"
              >
                <Tab icon={<SendIcon />} label="Contact" />
                <Tab icon={<QuestionIcon />} label="Ask Question" />
                <Tab icon={<FeatureIcon />} label="Request Feature" />
              </Tabs>
            </Box>

            <Box component="form" onSubmit={handleSubmit} sx={{ p: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                {tabValue === 0 && "General Contact Form"}
                {tabValue === 1 && "Submit a Technical Question"}
                {tabValue === 2 && "Suggest a New Feature"}
              </Typography>

              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Your Email"
                    required
                    type="email"
                    value={formData.email}
                    onChange={handleChange('email')}
                    placeholder="name@example.com"
                    helperText="We'll use this to reply to your request."
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Subject"
                    required
                    value={formData.subject}
                    onChange={handleChange('subject')}
                    placeholder={
                      tabValue === 0 ? "What is this regarding?" :
                      tabValue === 1 ? "What do you need help with?" :
                      "Describe the feature briefly"
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Message"
                    required
                    multiline
                    rows={6}
                    value={formData.message}
                    onChange={handleChange('message')}
                    placeholder={
                      tabValue === 0 ? "Tell us how we can help..." :
                      tabValue === 1 ? "Provide as much context as possible for your question..." :
                      "Describe the feature and why it would be valuable to you..."
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={20} /> : <SendIcon />}
                    sx={{ height: 56, mt: 2 }}
                  >
                    {submitting ? 'Sending...' : 'Send Submission'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Contact;
